"use client"

import { Button, Group, Loader, Stack, Text, ThemeIcon } from "@mantine/core"
import { modals } from "@mantine/modals"
import consola from "consola"
import { useCallback } from "react"
import { IconAlertCircle, IconBrandChrome } from "@tabler/icons-react"
import { usePostHog } from "posthog-js/react"

import { ensureChromeExtensionReady, getChromeExtensionInfo, getChromeExtensionInstallUrl } from "@/lib/chromeExtensionMessaging"
import { getChromeExtensionUpdateRequirement } from "@/lib/chromeExtensionUpdatePolicy"
import { showNotification } from "@/lib/notifications"

interface EnsureChromeExtensionReadinessParams {
  extensionId: string | null | undefined
  featureName: string
}

const extensionLogger = consola.withTag("chrome-extension")
const CHROME_EXTENSION_UPDATE_FLAG_KEY = "force-chrome-extension-update"
const FEATURE_FLAG_FRESH_TIMEOUT_MS = 2000
const FEATURE_FLAG_FRESH_POLL_INTERVAL_MS = 100

function delay(ms: number) {
  return new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms)
  })
}

async function getFreshFeatureFlagResult(posthog: ReturnType<typeof usePostHog>, key: string) {
  const immediateResult = posthog.getFeatureFlagResult(key, {
    send_event: false,
    fresh: true,
  })

  if (immediateResult !== undefined) {
    return immediateResult
  }

  posthog.reloadFeatureFlags()

  const deadline = Date.now() + FEATURE_FLAG_FRESH_TIMEOUT_MS

  while (Date.now() < deadline) {
    await delay(FEATURE_FLAG_FRESH_POLL_INTERVAL_MS)

    const nextResult = posthog.getFeatureFlagResult(key, {
      send_event: false,
      fresh: true,
    })

    if (nextResult !== undefined) {
      return nextResult
    }
  }

  return undefined
}

export function useChromeExtensionReadinessGate() {
  const posthog = usePostHog()

  const ensureReady = useCallback(
    async ({ extensionId, featureName }: EnsureChromeExtensionReadinessParams) => {
      extensionLogger.info("UI requested Chrome extension readiness", {
        featureName,
        extensionId,
      })

      const installUrl = getChromeExtensionInstallUrl(extensionId)
      const extensionInfo = await getChromeExtensionInfo(extensionId)
      if (extensionInfo) {
        const forceUpdateResult = await getFreshFeatureFlagResult(posthog, CHROME_EXTENSION_UPDATE_FLAG_KEY)
        const forceUpdateEnabled = forceUpdateResult?.enabled
        const forceUpdatePayload = forceUpdateResult?.payload
        const updateRequirement = getChromeExtensionUpdateRequirement({
          featureFlagEnabled: forceUpdateEnabled,
          featureFlagPayload: forceUpdatePayload,
          installedVersion: extensionInfo.version,
        })

        extensionLogger.info("Chrome extension update gate evaluated", {
          extensionId,
          featureName,
          installedVersion: extensionInfo.version,
          featureFlagEnabled: forceUpdateEnabled,
          featureFlagPayload: forceUpdatePayload,
          updateRequirement,
        })

        if (updateRequirement.requiresUpdate) {
          const modalId = modals.open({
            title: (
              <Group gap="sm" wrap="nowrap">
                <ThemeIcon size="md" radius="xl" variant="light" color="red">
                  <IconAlertCircle size={16} />
                </ThemeIcon>
                <Text fw={600}>Chrome extension update required</Text>
              </Group>
            ),
            centered: true,
            withCloseButton: false,
            closeOnClickOutside: false,
            children: (
              <Stack gap="md">
                <Text size="sm" c="dimmed">
                  Please update the Ring.io Chrome extension to continue. Click{" "}
                  <Text span fw={600} c="dark">
                    &quot;Remove from Chrome&quot;
                  </Text>
                  , then select{" "}
                  <Text span fw={600} c="dark">
                    &quot;Add to Chrome&quot;
                  </Text>{" "}
                  in the Web Store.
                </Text>
                <Group justify="flex-end" gap="md">
                  <Button variant="default" onClick={() => modals.close(modalId)}>
                    Cancel
                  </Button>
                  <Button
                    leftSection={<IconBrandChrome size={16} />}
                    onClick={() => {
                      if (installUrl) {
                        extensionLogger.info("Opening Chrome extension install URL from update modal", {
                          featureName,
                          extensionId,
                          installUrl,
                          installedVersion: extensionInfo.version,
                          requiredVersion: updateRequirement.minVersion,
                        })
                        window.open(installUrl, "_blank", "noopener,noreferrer")
                      }

                      modals.close(modalId)
                    }}
                    disabled={!installUrl}
                  >
                    Update Chrome Extension
                  </Button>
                </Group>
              </Stack>
            ),
          })

          return false
        }
      }

      extensionLogger.info("Opening Chrome extension readiness loading modal", {
        featureName,
        extensionId,
      })

      const loadingModalId = modals.open({
        title: "Preparing Ring.io Chrome Extension",
        centered: true,
        withCloseButton: false,
        closeOnClickOutside: false,
        closeOnEscape: false,
        children: (
          <Stack gap="md" align="center" py="xs">
            <Loader size="lg" />
            <Text size="sm" ta="center">
              Opening the extension and waiting for the side panel to be ready.
            </Text>
          </Stack>
        ),
      })

      const result = await ensureChromeExtensionReady(extensionId)

      extensionLogger.info("Closing Chrome extension readiness loading modal", {
        featureName,
        extensionId,
      })
      modals.close(loadingModalId)

      if (result.ok) {
        extensionLogger.info("Chrome extension readiness succeeded for UI request", {
          featureName,
          extensionId,
          openedSidePanel: result.openedSidePanel,
        })
        return true
      }

      extensionLogger.warn("Chrome extension readiness failed for UI request", {
        featureName,
        extensionId,
        result,
        installUrl,
      })

      if (result.reason === "not_ready") {
        showNotification({
          type: "error",
          title: "Ring.io Chrome Extension is still loading",
          message: "Keep the side panel open and try again in a few seconds.",
        })
        return false
      }

      const modalId = modals.open({
        title: (
          <Group gap="sm" wrap="nowrap">
            <ThemeIcon size="md" radius="xl" variant="light" color="blue">
              <IconBrandChrome size={16} />
            </ThemeIcon>
            <Text fw={600}>Chrome extension required</Text>
          </Group>
        ),
        size: "md",
        centered: true,
        withCloseButton: false,
        closeOnClickOutside: false,
        children: (
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              <Text span fw={600} c="dark">
                {featureName}
              </Text>{" "}
              uses the Ring.io Chrome extension. Install the extension, then try again.
            </Text>
            <Group justify="flex-end" gap="md">
              <Button variant="default" onClick={() => modals.close(modalId)}>
                Cancel
              </Button>
              <Button
                leftSection={<IconBrandChrome size={16} />}
                onClick={() => {
                  if (installUrl) {
                    extensionLogger.info("Opening Chrome extension install URL from readiness modal", {
                      featureName,
                      extensionId,
                      installUrl,
                    })
                    window.open(installUrl, "_blank", "noopener,noreferrer")
                  }

                  modals.close(modalId)
                }}
                disabled={!installUrl}
              >
                Install Chrome Extension
              </Button>
            </Group>
          </Stack>
        ),
      })

      return false
    },
    [posthog]
  )

  return {
    ensureReady,
  }
}
